import { KnowledgeBaseRepository } from "@app/repositories/GraphQLRepositories/KnowledgeBaseRepository";
import { tanstackUnitaryOptimisticUpdateOptionsMaker } from "@app/shared/utils/optimisticUpdates";
import {
    KnowledgeBaseItemFilter,
    KnowledgeBaseItemPatch,
    KnowledgeBaseItemType,
    UpdateCustomItemInput,
} from "@generated/client/graphql";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { activeTypeAtom } from "./ctx";
import {
    makeOptimisticKnowledgeBaseUpdate,
    makeSingleOptimisticKnowledgeBaseUpdate,
} from "./service";
const knowledgeBaseRepository = new KnowledgeBaseRepository();

export const knowledgeBaseKeys = {
    all: () => ["knowledgeBase"] as const,
    filtered: (filter: KnowledgeBaseItemFilter) =>
        [...knowledgeBaseKeys.all(), filter] as const,
};

export function useKnowledgeBase(filter: KnowledgeBaseItemFilter) {
    const { data, ...query } = useQuery({
        queryKey: knowledgeBaseKeys.filtered(filter),
        queryFn: () =>
            knowledgeBaseRepository.getKnowledgeBaseItems({ filter }),
    });
    const knowledgeBaseItems = data?.knowledgeBaseItems ?? [];
    return { knowledgeBaseItems, ...query };
}

export function useUpdateKnowledgeBaseItem(id: string) {
    const queryClient = useQueryClient();
    const activeType = useAtomValue(activeTypeAtom);

    const { mutate: updateKnowledgeBaseItem, ...mutation } = useMutation({
        mutationFn: (patch: KnowledgeBaseItemPatch) =>
            knowledgeBaseRepository.updateKnowledgeBaseItem(id, patch),
        ...tanstackUnitaryOptimisticUpdateOptionsMaker(
            () =>
                knowledgeBaseKeys.filtered({
                    type: activeType,
                }),
            makeSingleOptimisticKnowledgeBaseUpdate(id),
        ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: knowledgeBaseKeys.all(),
            });
        },
    });
    return { updateKnowledgeBaseItem, ...mutation };
}

export function useUpdateManyKnowledgeBaseItems() {
    const queryClient = useQueryClient();
    const activeType = useAtomValue(activeTypeAtom);

    const { mutate: updateManyKnowledgeBaseItems, ...mutation } = useMutation({
        mutationFn: ({
            ids,
            patch,
        }: {
            ids: string[];
            patch: KnowledgeBaseItemPatch;
        }) => knowledgeBaseRepository.updateManyKnowledgeBaseItems(ids, patch),
        ...tanstackUnitaryOptimisticUpdateOptionsMaker(
            () =>
                knowledgeBaseKeys.filtered({
                    type: activeType,
                }),
            makeOptimisticKnowledgeBaseUpdate(),
        ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: knowledgeBaseKeys.all(),
            });
        },
    });
    return { updateManyKnowledgeBaseItems, ...mutation };
}

export function useCreateCustomKnowledgeBaseItem() {
    const queryClient = useQueryClient();

    const { mutate: createCustomItem, ...mutation } = useMutation({
        mutationFn: (input: { description: string; value?: string }) =>
            knowledgeBaseRepository.createCustomKnowledgeBaseItem(
                input.description,
                input.value,
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: knowledgeBaseKeys.filtered({
                    type: KnowledgeBaseItemType.CustomItem,
                }),
            });
        },
    });
    return { createCustomItem, ...mutation };
}

export function useUpdateCustomKnowledgeBaseItem(id: string) {
    const queryClient = useQueryClient();

    const { mutate: updateCustomItem, ...mutation } = useMutation({
        mutationFn: (patch: UpdateCustomItemInput) =>
            knowledgeBaseRepository.updateCustomKnowledgeBaseItem(id, patch),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: knowledgeBaseKeys.filtered({
                    type: KnowledgeBaseItemType.CustomItem,
                }),
            });
        },
    });
    return { updateCustomItem, ...mutation };
}

export function useDeleteCustomKnowledgeBaseItem(id: string) {
    const queryClient = useQueryClient();
    const { mutate: deleteCustomItem, ...mutation } = useMutation({
        mutationFn: () =>
            knowledgeBaseRepository.deleteCustomKnowledgeBaseItem(id),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: knowledgeBaseKeys.filtered({
                    type: KnowledgeBaseItemType.CustomItem,
                }),
            });
        },
    });
    return { deleteCustomItem, ...mutation };
}
